import React, { useState, useContext } from 'react';
import { Grid } from '@material-ui/core';
import PropTypes from 'prop-types';

import { SectionHeader } from 'components';
import LayoutView from 'Views/Common/LayoutView';
import {
  AssociatedLocation,
  AssociatedPartner,
  AssociatedPlan,
  AssociatedRegion,
  CategorizedCommunicationTemplates,
  ContactDetails,
  Details,
  OnboardingDetails,
  UserManagement,
  Capabilities,
  ReportCapabilities,
  Documents,
  AssociatedGroup,
  LogoDetails,
  UploadMarketingMaterial,
  UcrFeesSchedule,
  PaymentCapabilities,
  PlatformCost,
  PmsConfiguration,
} from './components';
import ConfirmModal from 'Views/Common/ConfirmModal';
import SSOSettings from 'Views/Settings/components/SSOSettings';
import { USER_SERVICES } from 'Services';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import TerminalList from './components/TerminalList';
import { useCountryConfig } from 'hooks/useCountryConfig';
import { useInitializeCountryConfig } from 'hooks/useInitializeCountryConfig';

const VIEWS = {
  PARTNER: 'partner',
  GROUP: 'group',
  REGION: 'region',
  LOCATION: 'location',
};

const Info = (props) => {
  const {
    name,
    keyName,
    infoDetail,
    capabilities,
    reportCapabilities,
    createPlan: enableCreatePlan = true,
    autolinking,
    patientSearch,
    showEmployeeSupport,
    showAutoAssignPlan = false,
    showAllowGetAddons = false,
    showAllPlansCombinedToggle = false,
    showFeeScheduleSavingsToggle = false,
    showAllowFacility = false,
    showSeparateLocation = false,
    feesSchedule = false,
    showSSO = false,
    extraSections = null,
    documents,
    groupList,
    regionList,
    locationList,
    plansLoading,
    planList,
    marketingMaterialsList,
    data = {},
    users,
    onUpdate,
    locationPaymentMode = false,
  } = props;

  const { dispatch } = useContext(Context);
  const { config: countryConfig = {} } = useCountryConfig();
  useInitializeCountryConfig({ checkSingleCountry: false });

  const showGroups = [VIEWS.PARTNER].includes(keyName);
  const showTerminals = [VIEWS.LOCATION].includes(keyName) && countryConfig?.terminal?.enabled;
  const showRegions = [VIEWS.GROUP, VIEWS.PARTNER].includes(keyName);
  // This is the determination of like this feature or info is suitable for this hierarchy level
  const showLocations = [VIEWS.GROUP, VIEWS.PARTNER, VIEWS.REGION].includes(
    keyName,
  );
  const showPlans = [VIEWS.GROUP, VIEWS.LOCATION].includes(keyName);
  const showEmails = [VIEWS.GROUP, VIEWS.LOCATION].includes(keyName);
  const showMarketingMaterials = [VIEWS.GROUP].includes(keyName);
  const showPayments =
    [VIEWS.GROUP].includes(keyName) ||
    (keyName === VIEWS.LOCATION && locationPaymentMode);
  // Platform cost is group-level only (it was previously the group-only branch
  // inside PaymentCapabilities).
  const showPlatformCost = [VIEWS.GROUP].includes(keyName);
  const showPmsIntegration = [VIEWS.LOCATION].includes(keyName);
  const showDuplicateAppts = [VIEWS.LOCATION].includes(keyName);
  const showPatientConsentForm = [VIEWS.GROUP].includes(keyName);
  const showEsignature = [VIEWS.GROUP, VIEWS.LOCATION].includes(keyName);
  // Group AND location: the flag is three-state server-side, so a
  // location can opt out of a group that has it on.
  const showWalletPass = [VIEWS.GROUP, VIEWS.LOCATION].includes(keyName);
  const showPrivateOnly = [VIEWS.LOCATION].includes(keyName);
  // Membership-campaign (email/SMS) is a group-level capability only.
  const showMembershipCampaign = [VIEWS.GROUP].includes(keyName);

  const { partner_id, group_id, region_id, location_id, location_name } = infoDetail;
  const hierarchyDetails = {
    name,
    partner_id,
    group_id,
    region_id,
    location_id,
    location_name
  };

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [userData, setUserData] = useState({});
  const [isResetLoading, setResetLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const openConfirmPopup = () => setIsPopupOpen(true);
  const closeConfirmPopup = () => setIsPopupOpen(false);

  const onResetPasswordClick = (user) => {
    setUserData(user);

    openConfirmPopup();
  };

  const onResetClick = async () => {
    if (isResetLoading) return;

    setResetLoading(true);

    try {
      const userId = userData?.id || '';

      if (!userId) {
        setGlobalNotification('error', 'User ID is not available');
        setResetLoading(false);
        return;
      }

      const response = await USER_SERVICES.resetDefaultPasswordV2(userId);

      if (response?.type === 'success') {
        closeConfirmPopup();

        setGlobalNotification('success', 'Password reset successfully');
      } else {
        throw response?.message || response?.error;
      }
    } catch (err) {
      setGlobalNotification(
        'error',
        err || 'Error while reseting the password',
      );
    }
    setResetLoading(false);
  };

  const setGlobalNotification = (severity, message) =>
    dispatch({
      type: ADD_NOTIFICATION,
      payload: {
        notification: {
          severity,
          message,
        },
      },
    });

  const saveSettingsData = async (type = '', settingsData = {}) => {
    setIsLoading(type);

    const levelPayload = {
      [data?.settings?.setting_level]: {
        location_id,
        region_id,
        group_id,
        partner_id,
      },
    };

    try {
      const payload = { ...levelPayload, setting: settingsData };

      const response = await USER_SERVICES.updateLevels(
        payload,
        data?.settings?.setting_level,
      );

      if (response?.type === 'success') {
        setGlobalNotification(
          'success',
          'Report Capability updated successfully',
        );

        onUpdate?.('Settings');
      } else {
        throw response?.message || response?.error;
      }
    } catch (error) {
      setGlobalNotification(
        'error',
        error || 'Error updating the capability details',
      );
    }

    setIsLoading(false);
  };

  const handleSwitchChange = async (event) => {
    const { name: eName, checked } = event.target;

    const { additional_report = {} } = data?.settings || {};

    let settingsData = {
      additional_report: {
        ...additional_report,
        [eName]: checked,
      },
    };

    saveSettingsData(eName, settingsData);
  };

  if (!Object.keys(countryConfig).length) return <></>;

  return (
    <>
      <LayoutView.DocumentTitle title={`${name} Info`} />

      <SectionHeader
        title={`${name} Info`}
        titleVariant="h4"
        titleComponent="h4"
        gutterBottom
      />

      <Grid container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <AssociatedPartner
            infoDetail={infoDetail}
            accountDetails={data?.account}
            countryConfig={countryConfig}
          />
        </Grid>

        <Grid item xs={12}>
          <Details name={name} data={data} onUpdate={onUpdate} countryConfig={countryConfig} />
        </Grid>

        {Boolean(documents) && (
          <Grid item xs={12}>
            <LogoDetails
              levelData={data?.level_data}
              settings={data?.settings}
              onUpdate={onUpdate}
              countryConfig={countryConfig}
            />
          </Grid>
        )}

        {showTerminals ? (
          <Grid item xs={12}>
            <TerminalList locationId={location_id} countryConfig={countryConfig} />
          </Grid>
        ) : (
          <></>
        )}

        <Grid item xs={12}>
          <ContactDetails
            name={name}
            subHeading="This contact will be used as the Stripe contact and will also receive notifications and reports"
            contactMembers={data?.level_data?.contact_members || []}
            level_data={data?.level_data}
            settings={data?.settings}
            onUpdate={onUpdate}
            countryConfig={countryConfig}
          />
        </Grid>

        {showMarketingMaterials && (
          <Grid item xs={12}>
            <UploadMarketingMaterial
              group_id={group_id}
              data={marketingMaterialsList}
              onUpdate={() => onUpdate('Settings')}
              countryConfig={countryConfig}
            />
          </Grid>
        )}

        {showEmails && (
          <>
            <Grid item xs={12}>
              <CategorizedCommunicationTemplates
                level={keyName}
                group_id={group_id}
                location_id={location_id}
                title="Loop - Email Templates"
              />
            </Grid>
            <Grid item xs={12}>
              <CategorizedCommunicationTemplates
                level={keyName}
                group_id={group_id}
                location_id={location_id}
                isSync={true}
                title="Sync - Email Templates"
              />
            </Grid>
          </>
        )}

        {data?.account?.payouts_enabled && (
          <Grid item xs={12}>
            <OnboardingDetails name={name} details={data} onUpdate={onUpdate} />
          </Grid>
        )}

        {showPmsIntegration && (
          <Grid item xs={12}>
            <PmsConfiguration />
          </Grid>
        )}

        {Boolean(capabilities) && (
          <Grid item xs={12}>
            <Capabilities
              name={name}
              levelData={data?.level_data}
              settings={data?.settings}
              groupSettings={data?.group_settings}
              onUpdate={() => onUpdate('Settings')}
              autolinking={autolinking}
              patientSearch={patientSearch}
              showEmployeeSupport={showEmployeeSupport}
              showPmsIntegration={showPmsIntegration}
              showDuplicateAppts={showDuplicateAppts}
              showAutoAssignPlan={showAutoAssignPlan}
              showAllowGetAddons={showAllowGetAddons}
              showAllPlansCombinedToggle={showAllPlansCombinedToggle}
              showFeeScheduleSavingsToggle={showFeeScheduleSavingsToggle}
              planList={planList}
              showAllowFacility={showAllowFacility}
              showSeparateLocation={showSeparateLocation}
              showPatientConsent={showPatientConsentForm}
              showEsignature={showEsignature}
              showWalletPass={showWalletPass}
              showPrivateOnly={showPrivateOnly}
              showMembershipCampaignEmail={showMembershipCampaign}
              showMembershipCampaignSms={showMembershipCampaign}
            />
          </Grid>
        )}

        {Boolean(showPayments) && (
          <Grid item xs={12} className="position-relative">
            <PaymentCapabilities
              isLocationPaymentMode={keyName === VIEWS.LOCATION}
              levelData={data?.level_data}
              settings={data?.settings}
              onUpdate={() => onUpdate('Settings')}
              countryConfig={countryConfig}
            />
          </Grid>
        )}

        {Boolean(showPlatformCost) && (
          <Grid item xs={12} className="position-relative">
            <PlatformCost
              levelData={data?.level_data}
              locationList={locationList}
              onUpdate={() => onUpdate('Settings')}
            />
          </Grid>
        )}

        {Boolean(reportCapabilities) && (
          <Grid item xs={12}>
            <ReportCapabilities
              type={'info'}
              data={data?.settings?.additional_report}
              handleChange={handleSwitchChange}
              isLoading={isLoading}
              countryConfig={countryConfig}
            />
          </Grid>
        )}

        {Boolean(documents) && (
          <Grid item xs={12}>
            <Documents
              levelData={data?.level_data}
              settings={data?.settings}
              onUpdate={onUpdate}
              countryConfig={countryConfig}
            />
          </Grid>
        )}
        {Boolean(feesSchedule) && (
          <Grid item xs={12}>
            <UcrFeesSchedule
              levelData={data?.level_data}
              settings={data?.settings}
              onUpdate={onUpdate}
              countryConfig={countryConfig}
            />
          </Grid>
        )}

        {showSSO && (
          <Grid item xs={12}>
            <SSOSettings level={keyName} levelId={infoDetail[`${keyName}_id`]} partnerId={partner_id} groupId={group_id} />
          </Grid>
        )}

        {/* A slot for sections the owning view supplies, so Info does
            not grow a flag and an import per feature. */}
        {extraSections}

        <Grid item xs={12}>
          <UserManagement
            users={users}
            onUpdate={() => onUpdate('User')}
            handleResetPassword={onResetPasswordClick}
            countryConfig={countryConfig}
            isSSOOnly={!(data?.effective_auth_methods || ['password']).includes('password')}
          />
        </Grid>

        {showGroups && (
          <Grid item xs={12}>
            <AssociatedGroup
              hierarchyDetails={hierarchyDetails}
              groupList={groupList}
              isCreationEnabled={true}
              countryConfig={countryConfig}
            />
          </Grid>
        )}

        {showRegions && (
          <Grid item xs={12}>
            <AssociatedRegion
              hierarchyDetails={hierarchyDetails}
              regionList={regionList}
              isCreationEnabled={true}
              countryConfig={countryConfig}
            />
          </Grid>
        )}

        {showLocations && (
          <Grid item xs={12}>
            <AssociatedLocation
              hierarchyDetails={hierarchyDetails}
              locationList={locationList}
              isCreationEnabled={true}
              countryConfig={countryConfig}
            />
          </Grid>
        )}

        {showPlans && (
          <Grid item xs={12}>
            <AssociatedPlan
              hierarchyDetails={hierarchyDetails}
              plansLoading={plansLoading}
              planList={planList}
              isCreationEnabled={enableCreatePlan}
              onUpdate={() => onUpdate('Plans')}
              countryConfig={countryConfig}
              settings={
                keyName === VIEWS.LOCATION ? data?.settings : undefined
              }
            />
          </Grid>
        )}

        <ConfirmModal
          isOpen={isPopupOpen}
          closeModal={closeConfirmPopup}
          emitConfirmEvent={onResetClick}
          isLoading={isResetLoading}
          options={{
            title: `Reset password`,
            description: (
              <>
                Are you sure you want to reset <br /> the password for{' '}
                {userData?.name}?
              </>
            ),
            confirmText: 'Confirm',
            cancelText: 'Cancel',
          }}
        />
      </Grid>
    </>
  );
};

Info.propTypes = {
  name: PropTypes.string.isRequired,
  keyName: PropTypes.string.isRequired,
  infoDetail: PropTypes.object,
  capabilities: PropTypes.bool,
  reportCapabilities: PropTypes.bool,
  createPlan: PropTypes.bool,
  viewPlan: PropTypes.bool,
  autolinking: PropTypes.bool,
  patientSearch: PropTypes.bool,
  showEmployeeSupport: PropTypes.bool,
  showAutoAssignPlan: PropTypes.bool,
  showAllowGetAddons: PropTypes.bool,
  showAllPlansCombinedToggle: PropTypes.bool,
  showFeeScheduleSavingsToggle: PropTypes.bool,
  showAllowFacility: PropTypes.bool,
  showSeparateLocation: PropTypes.bool,
  documents: PropTypes.bool,
  feesSchedule: PropTypes.bool,
  showSSO: PropTypes.bool,
  extraSections: PropTypes.node,
  groupList: PropTypes.array,
  regionList: PropTypes.array,
  locationList: PropTypes.array,
  plansLoading: PropTypes.bool,
  planList: PropTypes.array,
  marketingMaterialsList: PropTypes.array,
  data: PropTypes.object,
  users: PropTypes.array,
  onUpdate: PropTypes.func,
  locationPaymentMode: PropTypes.bool,
};

export default Info;
